import React, { useState, useRef, useCallback } from 'react'
import { withJsonFormsControlProps } from '@jsonforms/react'
import { Box, Typography } from '@mui/material'
import { CloudUpload, InsertDriveFile, Clear } from '@mui/icons-material'
import { IconButton } from '@mui/material'

/**
 * Standalone file input control that doesn't depend on react-admin/react-hook-form.
 * Used in EMP external auth flow where there's no FormProvider context.
 * Styled to match react-admin's FileInput dropzone.
 */
const StandaloneFileInput = ({ handleChange, path, label }) => {
    const [fileName, setFileName] = useState('')
    const [isDragOver, setIsDragOver] = useState(false)
    const fileInputRef = useRef(null)

    const getBase64DataFromFile = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.readAsDataURL(file)
            // Split the value because first half is mimetype info, second half is actual data
            reader.onload = () => resolve(reader.result.split(',', 2)[1])
            reader.onerror = (error) => reject(error)
        })
    }

    const processFile = useCallback(
        async (file) => {
            if (file) {
                const val = await getBase64DataFromFile(file)
                setFileName(file.name)
                handleChange(path, val)
            }
        },
        [handleChange, path]
    )

    const onFileChange = useCallback(
        async (e) => {
            const targetFile = e.target.files?.[0]
            await processFile(targetFile)
        },
        [processFile]
    )

    const handleDrop = useCallback(
        async (e) => {
            e.preventDefault()
            e.stopPropagation()
            setIsDragOver(false)
            const file = e.dataTransfer.files?.[0]
            await processFile(file)
        },
        [processFile]
    )

    const handleDragOver = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragOver(true)
    }

    const handleDragLeave = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragOver(false)
    }

    const handleClick = () => {
        fileInputRef.current?.click()
    }

    const handleRemove = useCallback(
        (e) => {
            e.stopPropagation()
            setFileName('')
            handleChange(path, '')
            if (fileInputRef.current) {
                fileInputRef.current.value = ''
            }
        },
        [handleChange, path]
    )

    return (
        <Box sx={{ mb: 2 }}>
            <input
                type="file"
                ref={fileInputRef}
                onChange={onFileChange}
                style={{ display: 'none' }}
            />
            <Box
                onClick={handleClick}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                sx={{
                    border: '1px dashed',
                    borderColor: isDragOver ? 'primary.main' : 'grey.400',
                    borderRadius: 1,
                    backgroundColor: isDragOver
                        ? 'action.hover'
                        : 'background.paper',
                    padding: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'action.hover',
                    },
                }}
            >
                {fileName ? (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                        }}
                    >
                        <InsertDriveFile color="primary" />
                        <Typography variant="body2">{fileName}</Typography>
                        <IconButton size="small" onClick={handleRemove}>
                            <Clear fontSize="small" />
                        </IconButton>
                    </Box>
                ) : (
                    <>
                        <CloudUpload
                            sx={{ fontSize: 48, color: 'grey.500', mb: 1 }}
                        />
                        <Typography variant="body2" color="textSecondary">
                            {label || 'Drop a file here, or click to select'}
                        </Typography>
                    </>
                )}
            </Box>
        </Box>
    )
}

export default withJsonFormsControlProps(StandaloneFileInput)
